import React, { useEffect } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { ShieldOff, Trash2 } from "lucide-react";
import { BlockUsersAction, DeleteUsersAction } from "store/middlewares/user";
import { closeAdminModal } from "store/slices/common/adminModal.slice";
import { CustomSelect, InputTextarea } from "components/atoms";
import { Button } from "components/ui";
import { GetAllRejectReasonAction } from "store/middlewares/dataManagement/rejectReason";

// Text + icon for each action. Edit the points so they match what your backend really does.
const CONTENT = {
  blocked: {
    title: "Block this user?",
    Icon: ShieldOff,
    submitText: "Block user",
    points: [
      "They are signed out of every device right away.",
      "They can’t sign in to Mail, Chat or Drive.",
      "You can unblock them at any time.",
    ],
  },
  delete: {
    title: "Delete this user?",
    Icon: Trash2,
    submitText: "Delete user",
    points: [
      "They are signed out of every device right away.",
      "They can’t sign in to Mail, Chat or Drive.",
      "You can restore them later from the Users list.",
    ],
  },
};

const ModalBlockDeleteReason = ({ type, id, filter }) => {
  const dispatch = useDispatch();
  const { rejectReasons } = useSelector((state) => state.rejectReasons);
  const { title, Icon, submitText, points } =
    CONTENT[type === "blocked" ? "blocked" : "delete"];

  useEffect(() => {
    dispatch(GetAllRejectReasonAction({ type: "deleteAccount", page: 1 }));
  }, [dispatch]);

  const options =
    rejectReasons?.data?.map((item) => ({
      label: item.note,
      value: item.note,
    })) ?? [];

  const allOptions = [...options, { label: "Other", value: "other" }];

  const handleSubmit = (values) => {
    const payload = {
      id,
      reason:
        values.reason === "other" ? values.otherReason.trim() : values.reason,
      filter,
    };

    if (type === "blocked") {
      dispatch(BlockUsersAction(payload));
    } else {
      dispatch(DeleteUsersAction(payload));
    }
  };

  const formik = useFormik({
    initialValues: {
      reason: "",
      otherReason: "",
    },
    validationSchema: Yup.object({
      reason: Yup.string().required("Reason is required"),
      otherReason: Yup.string().when("reason", {
        is: "other",
        then: (schema) => schema.required("Please enter reason"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: handleSubmit,
  });

  return (
    <form onSubmit={formik.handleSubmit} className="grid gap-5">
      {/* Title with icon */}
      <div className="flex items-center gap-3.5">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
          <Icon className="size-5" aria-hidden="true" />
        </div>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
      </div>

      {/* What happens */}
      <ul className="grid gap-2">
        {points.map((point) => (
          <li
            key={point}
            className="flex gap-2.5 text-sm leading-6 text-secondary-foreground"
          >
            <span className="mt-2.5 size-1.5 shrink-0 rounded-full bg-muted-foreground" />
            {point}
          </li>
        ))}
      </ul>

      {/* Reason (required, same rules as before) */}
      <div>
        <label className="text-sm font-semibold">Reason</label>
        <CustomSelect
          className="rounded-lg border border-solid w-full px-2 py-2 mt-2"
          name="reason"
          options={allOptions}
          value={formik.values.reason}
          placeholder="Select a reason"
          onChange={(option) => {
            formik.setFieldValue("reason", option || "");
          }}
          onBlur={() => formik.setFieldTouched("reason", true)}
          error={formik.errors.reason && formik.touched.reason}
          errorMsg={formik.errors.reason}
          variant="lg"
        />
      </div>

      {formik.values.reason === "other" && (
        <InputTextarea
          textareaClassName="rounded-lg border border-solid w-full px-2 py-2 mt-2 no-scrollbar"
          label={"Other Reason"}
          isRequired={false}
          name="otherReason"
          maxLength={250}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.errors.otherReason && formik.touched.otherReason}
          errorMsg={formik.errors.otherReason}
          value={formik.values.otherReason}
          placeholder="Enter reason"
        />
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => dispatch(closeAdminModal())}
        >
          Cancel
        </Button>
        <Button type="submit" variant="destructive" size="lg">
          {submitText}
        </Button>
      </div>
    </form>
  );
};

export default ModalBlockDeleteReason;